import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Scissors,
  User,
  Building2,
  Calendar,
  CreditCard,
  Gift,
  Star,
  Users,
  Check,
  Play,
  Pause,
  Crown,
  Sparkles,
  DollarSign,
  Clock,
  Camera,
  Share2,
  BarChart3,
  Shield
} from "lucide-react";

const AppIntroduction = ({ onComplete, autoPlay = true, showCloseButton = true }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);

  const slides = [
    {
      id: "welcome",
      title: "Welcome to MyHairChair",
      subtitle: "Your Chair. Your Rules. Your Rewards.",
      description: "The all-in-one platform for beauty professionals and clients. Manage bookings, payments, and grow your business.",
      icon: Scissors,
      color: "from-amber-400 to-yellow-500",
      features: [
        { icon: Calendar, text: "Easy Online Booking" },
        { icon: CreditCard, text: "Multiple Payment Options" },
        { icon: Gift, text: "Loyalty Rewards Program" },
        { icon: Shield, text: "Secure & Reliable" }
      ],
      visual: "welcome"
    },
    {
      id: "clients",
      title: "For Clients",
      subtitle: "Find Your Perfect Stylist",
      description: "Discover talented barbers and stylists near you. Book appointments, earn rewards, and never have a bad hair day again.",
      icon: User,
      color: "from-blue-400 to-cyan-500",
      features: [
        { icon: Users, text: "Browse local stylists & barbers" },
        { icon: Calendar, text: "Book appointments 24/7" },
        { icon: Gift, text: "Earn loyalty points on every visit" },
        { icon: Star, text: "Rate and review your experience" }
      ],
      visual: "client"
    },
    {
      id: "stylists",
      title: "For Barbers & Stylists",
      subtitle: "Grow Your Business",
      description: "Take control of your career. Manage your schedule, accept payments, and build your client base all in one place.",
      icon: Scissors,
      color: "from-amber-400 to-orange-500",
      features: [
        { icon: Clock, text: "Set your own schedule" },
        { icon: DollarSign, text: "Accept multiple payment methods" },
        { icon: Camera, text: "Showcase your work with a gallery" },
        { icon: Share2, text: "Share to social media" }
      ],
      visual: "stylist"
    },
    {
      id: "owners",
      title: "For Salon Owners",
      subtitle: "Manage Your Empire",
      description: "Run your salon or barbershop efficiently. Collect booth rent, manage your team, and track performance.",
      icon: Building2,
      color: "from-purple-400 to-pink-500",
      features: [
        { icon: Building2, text: "Booth/chair management" },
        { icon: CreditCard, text: "Automated rent collection" },
        { icon: Users, text: "Multi-stylist dashboard" },
        { icon: BarChart3, text: "Team analytics & reports" }
      ],
      visual: "owner"
    },
    {
      id: "tiers",
      title: "Choose Your Plan",
      subtitle: "30 Days Free, Then Simple Pricing",
      description: "Two simple plans for every stage of your business. Both include a 30-day free trial.",
      icon: Crown,
      color: "from-amber-400 to-yellow-500",
      tiers: [
        {
          name: "Pro",
          price: "$29.99",
          period: "/mo",
          color: "bg-gradient-to-r from-amber-500 to-yellow-500",
          highlight: true,
          features: ["30 days FREE", "Unlimited clients", "CRM Dashboard", "Pro Mini-Site", "All payments"]
        },
        {
          name: "Elite",
          price: "$49.99",
          period: "/mo",
          color: "bg-gradient-to-r from-purple-500 to-pink-500",
          features: ["30 days FREE", "Everything in Pro", "Staff Management", "Rent collection", "0% fees"]
        }
      ],
      visual: "tiers"
    },
    {
      id: "getstarted",
      title: "Ready to Get Started?",
      subtitle: "Join thousands of beauty professionals",
      description: "Start your 30-day free trial today. No credit card required to start.",
      icon: Sparkles,
      color: "from-amber-400 to-yellow-500",
      cta: true,
      stats: [
        { value: "10K+", label: "Professionals" },
        { value: "98%", label: "Satisfaction" },
        { value: "30", label: "Days Free" }
      ],
      visual: "cta"
    }
  ];

  // Auto-advance slides
  useEffect(() => {
    if (!isPlaying) return;
    
    const timer = setInterval(() => {
      setCurrentSlide((prev) => {
        if (prev >= slides.length - 1) {
          setIsPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 6000);

    return () => clearInterval(timer);
  }, [isPlaying, slides.length]);

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsPlaying(false);
  };

  const slide = slides[currentSlide];
  const IconComponent = slide.icon;

  return (
    <div className="relative bg-gradient-to-br from-stone-900 via-stone-900 to-stone-950 rounded-2xl overflow-hidden">
      {/* Header with controls */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsPlaying(!isPlaying)}
            className="w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 text-white"
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <span className="text-xs text-white/60">
            {currentSlide + 1} / {slides.length}
          </span>
        </div>
        
        {showCloseButton && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onComplete}
            className="w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 text-white"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 z-10 flex gap-1 p-2">
        {slides.map((_, idx) => (
          <div
            key={idx}
            className="flex-1 h-1 rounded-full bg-white/20 overflow-hidden cursor-pointer"
            onClick={() => goToSlide(idx)}
          >
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                idx < currentSlide
                  ? "bg-amber-400 w-full"
                  : idx === currentSlide
                  ? "bg-amber-400"
                  : "bg-transparent w-0"
              }`}
              style={{
                width: idx === currentSlide && isPlaying ? "100%" : idx < currentSlide ? "100%" : "0%",
                transition: idx === currentSlide && isPlaying ? "width 6s linear" : "width 0.3s"
              }}
            />
          </div>
        ))}
      </div>

      {/* Main content */}
      <div className="pt-16 pb-8 px-6 min-h-[500px] flex flex-col">
        {/* Icon and titles */}
        <div className="text-center mb-6">
          <div className={`inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br ${slide.color} items-center justify-center mb-4 shadow-lg`}>
            <IconComponent className="w-8 h-8 text-black" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">{slide.title}</h2>
          <p className={`text-lg font-medium bg-gradient-to-r ${slide.color} bg-clip-text text-transparent`}>
            {slide.subtitle}
          </p>
          <p className="text-stone-400 mt-3 max-w-lg mx-auto text-sm md:text-base">
            {slide.description}
          </p>
        </div>

        {/* Slide-specific content */}
        <div className="flex-1 flex items-center justify-center">
          {/* Features list */}
          {slide.features && (
            <div className="grid grid-cols-2 gap-4 max-w-md w-full">
              {slide.features.map((feature, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-3 bg-stone-800/50 rounded-xl border border-stone-700/50"
                >
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${slide.color} bg-opacity-20 flex items-center justify-center`}>
                    <feature.icon className="w-5 h-5 text-amber-400" />
                  </div>
                  <span className="text-sm text-stone-300">{feature.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Tiers display */}
          {slide.tiers && (
            <div className="grid grid-cols-2 gap-4 max-w-xl w-full">
              {slide.tiers.map((tier, idx) => (
                <div
                  key={idx}
                  className={`relative p-4 rounded-xl border ${
                    tier.highlight 
                      ? "border-amber-500/50 bg-amber-500/10" 
                      : "border-stone-700 bg-stone-800/50"
                  }`}
                >
                  {tier.highlight && (
                    <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-amber-500 text-black text-xs">
                      Popular
                    </Badge>
                  )}
                  <h4 className="font-bold text-white text-center mb-1">{tier.name}</h4>
                  <div className="text-center mb-3">
                    <span className="text-2xl font-bold text-white">{tier.price}</span>
                    {tier.period && <span className="text-stone-400 text-sm">{tier.period}</span>}
                  </div>
                  <ul className="space-y-1">
                    {tier.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-1 text-xs text-stone-400">
                        <Check className={`w-3 h-3 ${tier.highlight ? "text-amber-400" : "text-green-400"}`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* CTA slide */}
          {slide.cta && (
            <div className="text-center">
              <div className="flex justify-center gap-6 mb-8">
                {slide.stats.map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <div className="text-3xl font-bold text-amber-400">{stat.value}</div>
                    <div className="text-xs text-stone-400">{stat.label}</div>
                  </div>
                ))}
              </div>
              <Button
                onClick={onComplete}
                className="bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-bold px-8 py-3 text-lg hover:opacity-90"
              >
                Start 30-Day Free Trial
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-stone-500 text-xs mt-3">No credit card required</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation arrows */}
      <div className="absolute bottom-4 left-0 right-0 flex justify-between px-4">
        <Button
          variant="ghost"
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back
        </Button>
        
        {currentSlide < slides.length - 1 ? (
          <Button
            onClick={nextSlide}
            className="bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-medium hover:opacity-90"
          >
            Next
            <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={onComplete}
            className="bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-medium hover:opacity-90"
          >
            Get Started
            <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default AppIntroduction;
